import PropTypes from 'prop-types'
import React from 'react'
import cx from 'classnames'
import { connect } from 'react-redux'
import moment from 'moment'

import { ReserveTooltipBody } from '../ReserveTooltip/components/ReserveTooltipBody'
import { Tooltip } from 'react-wot-bootstrap'

import { toCssClass } from 'helpers'
import { upgradeAvailability } from 'helpers/buildings'
import { getTargetType, getGroupByReserve, getSortedProductionReserves } from 'helpers/reserves'
import { getEndDateMessage } from 'helpers/datetime'
import isFunction from 'lodash/isFunction'
import {
    BUILDING_TITLES,
    LEVEL_STRING, LEVELS_RANGE_STRING,
    RESERVE_TITLES,
} from 'helpers/i18n.jsx'

import {
    RESERVE_QUEUE,
    RESERVE_QUEUE_END,
    UPGRADE_AVAILABLE,
    AVAILABLE_RESERVES,
    BUILDING_LEVEL,
} from './constants.jsx'

import styles from './BuildingTooltip.scss'
import { RESERVE_TYPES } from 'constants'

export class BuildingTooltip extends React.Component {
    static propTypes = {
        id: PropTypes.string.isRequired,
        building: PropTypes.object.isRequired,
        settings: PropTypes.object.isRequired,
        isUpgradeAvailable: PropTypes.bool.isRequired,
        reserveQueue: PropTypes.arrayOf(PropTypes.object).isRequired,
        reserveType: PropTypes.string.isRequired,
        bounds: PropTypes.object,
    }
    constructor(props) {
        super(props)
        this.state = { upcomingReserves: 0, productionEnds: null, uniqueLevels: [] }
    }
    componentWillReceiveProps(nextProps) {
        this.processReserveQueue(nextProps.reserveQueue)
    }
    componentWillMount() {
        this.processReserveQueue(this.props.reserveQueue)
    }
    componentWillUnmount() {
        if (this.timerId) {
            clearTimeout(this.timerId)
        }
    }
    processReserveQueue(reserveQueue) {
        if (this.timerId) {
            clearTimeout(this.timerId)
        }
        const reservesInProgress = getSortedProductionReserves(reserveQueue)

        const upcomingReserves = reservesInProgress.length
        const productionEnds = !!upcomingReserves && reservesInProgress[reservesInProgress.length - 1].productionEnds

        const uniqueLevels = Array
            .from(new Set(reservesInProgress.map(({ level }) => level)))
            .sort((a, b) => b - a)

        if (upcomingReserves) {
            this.timerId = setTimeout(
                this.processReserveQueue.bind(this, reserveQueue), reservesInProgress[0].productionEnds - Date.now()
            )
        }

        if (upcomingReserves !== this.state.upcomingReserves) {
            setTimeout(() => {
                if (this._tooltip && this._tooltip.state.visible) {
                    this._tooltip.show()
                }
            }, 0)
        }

        this.setState({ productionEnds, upcomingReserves, uniqueLevels })
    }
    render() {
        const { level, type } = this.props.building
        const reserveType = this.props.reserveType
        const { uniqueLevels } = this.state

        // disable tooltip for inspiration and artillery strike in usual browser
        if (!isFunction(window.jsHostQuery) &&
            (reserveType === RESERVE_TYPES.INSPIRATION || reserveType === RESERVE_TYPES.ARTILLERY_STRIKE)) {
            return null
        }
        const reserveInfo = this.props.settings.reserves[reserveType]

        const reserveGroup = getGroupByReserve(reserveType, this.props.settings)
        const bonusValuesArray = Object.values(reserveInfo.bonus.values[level - 1].battle_types)
        const targetType = getTargetType(reserveGroup, bonusValuesArray)
        const isReserveHasParameters = Boolean(reserveInfo.bonus.values[level].parameters)
        const reserveBodyNeeded = this.state.upcomingReserves === 0 || uniqueLevels.length === 1

        return (
            <Tooltip id={this.props.id} bounds={this.props.bounds} isWide ref={node => this._tooltip = node}>
                <div className={cx(styles.base, styles[`base__type-${targetType}`])}>
                    <div className={styles.header}>
                        <p className={styles.title}>{BUILDING_TITLES[type]}</p>
                        <p className={styles.level}>{BUILDING_LEVEL(level)}</p>
                    </div>
                    <div className={styles.divider} />
                    <div className={styles.reserve}>
                        <div className={styles.button}>
                            <div className={styles.thumbnail}>
                                <div className={cx(
                                    styles.image,
                                    styles[`image__${toCssClass(reserveType)}`]
                                )}
                                />
                            </div>
                        </div>
                        <div className={styles.reserveInfo}>
                            <p className={styles.reserveTitle}>{RESERVE_TITLES[reserveInfo.type]}</p>
                            <p className={styles.reserveLevel}>
                                {AVAILABLE_RESERVES(
                                    <span className={styles.reserveLevelValue}>
                                        {level === 1 ? LEVEL_STRING(level) : LEVELS_RANGE_STRING(1, level)}
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>
                    {reserveBodyNeeded && (
                        <ReserveTooltipBody
                            reserveType={reserveType}
                            reserveInfo={reserveInfo}
                            level={uniqueLevels.length === 1 ? uniqueLevels[0] : level}
                        />
                    )}
                    {Boolean(this.state.upcomingReserves) && (
                        <div className={styles.statusMessage}>
                            {(isReserveHasParameters || !reserveBodyNeeded) &&
                                <div className={cx(styles.divider, !reserveBodyNeeded && styles.divider__collapsed)}/>}
                            <p className={styles.status}>{RESERVE_QUEUE(this.state.upcomingReserves)}</p>
                            <p className={styles.statusLevels}>
                                {LEVEL_STRING(...uniqueLevels)}
                            </p>
                            <p className={cx(styles.statusText, styles.finishText)}>
                                {RESERVE_QUEUE_END(
                                    <span className={styles.finishTime}>
                                        {getEndDateMessage(moment(this.state.productionEnds))}
                                    </span>
                                )}
                            </p>
                        </div>
                    )}
                    {Boolean(!this.state.upcomingReserves && this.props.isUpgradeAvailable) && (
                        <div className={cx(styles.statusMessage, styles.statusMessage__positive)}>
                            {isReserveHasParameters && <div className={styles.divider} />}
                            <p className={styles.status}>{UPGRADE_AVAILABLE}</p>
                        </div>
                    )}
                </div>
            </Tooltip>
        )
    }
}

export const mapStateToProps = (state, {
    id,
    building,
}) => {
    const { settings } = state

    const reserveType = state.settings.buildings[building.type].reserve_type
    const reserveQueue = getSortedProductionReserves(state.clanReserves[reserveType])
    const isUpgradeAvailable = upgradeAvailability(state, building)

    return {
        id,
        building,
        reserveQueue,
        reserveType,
        settings,
        isUpgradeAvailable,
    }
}

export default connect(mapStateToProps)(BuildingTooltip)



// WEBPACK FOOTER //
// ./components/BuildingTooltip/BuildingTooltip.jsx